import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { academyCourses, academyPosition, allAcademyLessons, findAcademyLesson, type AcademyLesson } from '../data/academy';
import { api, type AnalysisLine, type AnalysisResult } from '../lib/api';
import { applyMove, pieceNames, type Piece, type Side, type Square } from '../lib/janggi';
import { GameBoard } from './GameBoard';

const COMPLETED_KEY = 'academy-completed-v1';
const loadCompleted = (): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(COMPLETED_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  } catch { return []; }
};
const saveCompleted = (items: string[]) => localStorage.setItem(COMPLETED_KEY, JSON.stringify(items));
const lessonHref = (courseId: string, lessonId: string) => `/learn/${courseId}/${lessonId}`;
const squareKey = ({ file, rank }: Square) => `${file}:${rank}`;
const uciSquare = (square: Square) => `${String.fromCharCode(97 + square.file)}${10 - square.rank}`;
const parseUci = (uci: string) => {
  const match = uci.match(/^([a-i])(10|[1-9])([a-i])(10|[1-9])$/);
  if (!match) return null;
  return { from: { file: match[1].charCodeAt(0) - 97, rank: 10 - Number(match[2]) }, to: { file: match[3].charCodeAt(0) - 97, rank: 10 - Number(match[4]) } };
};
const notation = (piece: Piece, to: Square) => `${piece.side === 'cho' ? '초' : '한'}${pieceNames[piece.type]} ${piece.file + 1}·${piece.rank + 1}–${to.file + 1}·${to.rank + 1}`;

function AcademyTitle({ completed }: { completed: number }) {
  return <header className="academy-title">
    <div><span>장기마루 공식 학습 과정</span><h1>장기 아카데미</h1><p>설명을 읽고, 실제 장기알을 움직이고, Fairy-Stockfish의 후보 수와 평가로 자신의 판단을 검증하십시오.</p></div>
    <div className="academy-title__stat"><strong>{completed}</strong><span>/ 60강 완료</span></div>
  </header>;
}

export function AcademyPage() {
  const [completed] = useState(loadCompleted);
  const next = allAcademyLessons.find(({ lesson }) => !completed.includes(lesson.id)) || allAcademyLessons[0];
  const progress = Math.round(completed.length / allAcademyLessons.length * 100);
  return <div className="page academy-page">
    <AcademyTitle completed={completed.length}/>
    <section className="academy-resume">
      <div className="academy-resume__ring" style={{ '--progress': `${progress * 3.6}deg` } as React.CSSProperties}><strong>{progress}%</strong></div>
      <div><span>이어서 학습하기</span><h2>{next.lesson.title}</h2><p>{next.course.title} · {next.lesson.minutes}분 · 엔진 분석 포함</p></div>
      <Link to={lessonHref(next.course.id, next.lesson.id)}>계속하기</Link>
    </section>

    <section className="academy-method">
      <article><b>1</b><div><strong>개념 읽기</strong><span>한 레슨에 하나의 판단 기준</span></div></article>
      <article><b>2</b><div><strong>판에서 실습</strong><span>클릭과 드래그로 직접 착수</span></div></article>
      <article><b>3</b><div><strong>엔진으로 검증</strong><span>후보 수 3개와 변화도 비교</span></div></article>
      <article><b>4</b><div><strong>기보로 복습</strong><span>어느 수에서 평가가 변했는지 확인</span></div></article>
    </section>

    <div className="academy-course-grid">
      {academyCourses.map((course) => {
        const count = course.lessons.filter((item) => completed.includes(item.id)).length;
        return <section className="academy-course" key={course.id} style={{ '--course-colour': course.colour } as React.CSSProperties}>
          <header><span>{course.number}</span><div><small>{course.level} · 10강</small><h2>{course.title}</h2><p>{course.description}</p></div><strong>{count}/10</strong></header>
          <div className="academy-course__track"><i style={{ width: `${count * 10}%` }}/></div>
          <ol>{course.lessons.map((item, index) => <li key={item.id} className={completed.includes(item.id) ? 'complete' : ''}>
            <Link to={lessonHref(course.id, item.id)}><span>{completed.includes(item.id) ? '✓' : String(index + 1).padStart(2, '0')}</span><div><strong>{item.title}</strong><small>{item.objective}</small></div><b>{item.minutes}분</b></Link>
          </li>)}</ol>
        </section>;
      })}
    </div>
  </div>;
}

type LessonPosition = { pieces: Piece[]; sideToMove: Side; move?: string; moveUci?: string };
type LessonTab = 'lesson' | 'engine' | 'moves';

function lineScore(line: AnalysisLine | undefined, sideToMove: Side) {
  if (!line) return { text: '분석 대기', share: 50 };
  const score = line.score.value * (sideToMove === 'cho' ? 1 : -1);
  if (line.score.type === 'mate') return { text: `${score >= 0 ? '초' : '한'} 외통 수순`, share: score >= 0 ? 100 : 0 };
  const amount = Math.abs(score), side = score >= 0 ? '초' : '한';
  const text = amount < 25 ? '호각' : amount < 80 ? `${side} 미세 우세` : amount < 180 ? `${side} 우세` : `${side} 크게 우세`;
  return { text, share: Math.round(50 + 46 * Math.tanh(score / 220)) };
}

function formatEngineLine(line: AnalysisLine, pieces: Piece[]) {
  let board = pieces.map((piece) => ({ ...piece }));
  return line.pv.slice(0, 6).map((uci) => {
    const parsed = parseUci(uci);
    if (!parsed) return uci;
    const piece = board.find((item) => item.file === parsed.from.file && item.rank === parsed.from.rank);
    if (!piece) return '한 수 쉼';
    const label = notation(piece, parsed.to);
    board = applyMove(board, piece.id, parsed.to);
    return label;
  });
}

function LessonNotes({ lesson, status, onComplete }: { lesson: AcademyLesson; status: 'idle' | 'correct' | 'wrong'; onComplete: () => void }) {
  return <div className="study-notes">
    <span className="study-eyebrow">학습 목표</span><h2>{lesson.objective}</h2>
    <p>{lesson.principle}</p><p>{lesson.practice}</p>
    <aside><b>기억할 기준</b><p>{lesson.tip}</p></aside>
    {lesson.challenge ? <div className={`study-challenge study-challenge--${status}`}><span>직접 풀기</span><strong>{lesson.challenge.prompt}</strong>{status === 'correct' && <p>✓ {lesson.challenge.success}</p>}{status === 'wrong' && <p>다른 수입니다. 엔진 탭에서 이 수와 추천 수를 비교해 보십시오.</p>}</div> : <button className="study-complete-button" onClick={onComplete}>이 레슨 완료하기</button>}
  </div>;
}

export function AcademyLessonPage() {
  const { courseId, lessonId } = useParams();
  const found = findAcademyLesson(courseId, lessonId);
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(loadCompleted);
  const [timeline, setTimeline] = useState<LessonPosition[]>([]);
  const [index, setIndex] = useState(0);
  const [tab, setTab] = useState<LessonTab>('lesson');
  const [status, setStatus] = useState<'idle' | 'correct' | 'wrong'>('idle');
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [engineError, setEngineError] = useState('');
  const [perspective, setPerspective] = useState<Side>('cho');
  const requestId = useRef(0);

  const lesson = found?.lesson;
  useEffect(() => {
    if (!lesson) return;
    setTimeline([{ pieces: academyPosition(lesson.position, lesson.id), sideToMove: lesson.sideToMove }]);
    setIndex(0); setTab('lesson'); setStatus('idle'); setAnalysis(null); setEngineError(''); setPerspective('cho');
  }, [lesson?.id]);
  const current = timeline[index];

  useEffect(() => {
    if (!current) return;
    const id = ++requestId.current;
    setLoading(true); setEngineError('');
    const timer = window.setTimeout(() => api.analyse(current.pieces, current.sideToMove, 10, 3)
      .then((result) => { if (id === requestId.current) setAnalysis(result); })
      .catch(() => { if (id === requestId.current) setEngineError('엔진 분석을 불러오지 못했습니다. 판에서는 계속 실습할 수 있습니다.'); })
      .finally(() => { if (id === requestId.current) setLoading(false); }), 140);
    return () => window.clearTimeout(timer);
  }, [current]);

  if (!found || !lesson) return <Navigate to="/learn" replace/>;
  if (!current) return <div className="page"><p>레슨을 준비하고 있습니다.</p></div>;

  const course = found.course;
  const lessonIndex = course.lessons.findIndex((item) => item.id === lesson.id);
  const globalIndex = allAcademyLessons.findIndex((item) => item.lesson.id === lesson.id);
  const previous = allAcademyLessons[globalIndex - 1];
  const next = allAcademyLessons[globalIndex + 1];
  const markComplete = () => {
    if (completed.includes(lesson.id)) return;
    const updated = [...completed, lesson.id]; setCompleted(updated); saveCompleted(updated);
  };
  const move = (piece: Piece, to: Square) => {
    const nextPosition: LessonPosition = {
      pieces: applyMove(current.pieces, piece.id, to),
      sideToMove: current.sideToMove === 'cho' ? 'han' : 'cho',
      move: notation(piece, to), moveUci: `${uciSquare(piece)}${uciSquare(to)}`,
    };
    setTimeline((items) => [...items.slice(0, index + 1), nextPosition]);
    setIndex(index + 1); setAnalysis(null);
    if (index === 0 && lesson.challenge) {
      const correct = squareKey(piece) === squareKey(lesson.challenge.from) && squareKey(to) === squareKey(lesson.challenge.to);
      setStatus(correct ? 'correct' : 'wrong');
      if (correct) { markComplete(); setTab('lesson'); } else setTab('engine');
    }
  };
  const playUci = (uci: string) => {
    const parsed = parseUci(uci); if (!parsed) return;
    const piece = current.pieces.find((item) => item.file === parsed.from.file && item.rank === parsed.from.rank);
    if (piece) move(piece, parsed.to);
  };
  const engineMoves = (analysis?.legalMoves || []).map(parseUci).filter((item): item is { from: Square; to: Square } => Boolean(item) && squareKey(item!.from) !== squareKey(item!.to));
  const leading = lineScore(analysis?.lines[0], current.sideToMove);
  const courseCount = course.lessons.filter((item) => completed.includes(item.id)).length;
  const showCoordinateGuide = course.id === 'basics' && index === 0;
  const coordinateTarget: Square | null = showCoordinateGuide
    ? lesson.challenge?.to || (lesson.id === 'basics-01' ? { file: 3, rank: 2 } : null)
    : null;
  const boardGuides = coordinateTarget ? [
    ...(lesson.challenge ? [{ square: lesson.challenge.from, tone: 'origin' as const }] : []),
    { square: coordinateTarget, label: `${coordinateTarget.file + 1}선 ${coordinateTarget.rank + 1}단`, tone: 'target' as const },
  ] : undefined;
  const reset = () => { setTimeline([{ pieces: academyPosition(lesson.position, lesson.id), sideToMove: lesson.sideToMove }]); setIndex(0); setStatus('idle'); setAnalysis(null); setTab('lesson'); };
  const openAnalysis = () => {
    sessionStorage.setItem('analysis-import', JSON.stringify({ pieces: current.pieces, sideToMove: current.sideToMove }));
    navigate('/analysis');
  };

  return <div className="study-page">
    <header className="study-header">
      <Link to="/learn">← 전체 과정</Link><div><span>{course.number} · {course.title}</span><strong>{lesson.title}</strong></div><b>{lessonIndex + 1} / {course.lessons.length}</b>
    </header>
    <div className="study-layout">
      <aside className="study-chapters">
        <header><span style={{ background: course.colour }}>{course.number}</span><div><strong>{course.title}</strong><small>{courseCount}/10 완료</small></div></header>
        <div>{course.lessons.map((item, itemIndex) => <Link key={item.id} className={`${item.id === lesson.id ? 'active' : ''}${completed.includes(item.id) ? ' complete' : ''}`} to={lessonHref(course.id, item.id)}><span>{completed.includes(item.id) ? '✓' : itemIndex + 1}</span><div><strong>{item.title}</strong><small>{item.minutes}분</small></div></Link>)}</div>
      </aside>

      <main className="study-board-column">
        <section className="study-evaluation"><b>초</b><div><span style={{ width: `${leading.share}%` }}/><i style={{ left: `${leading.share}%` }}/></div><strong>{loading ? '분석 중' : leading.text}</strong><b>한</b></section>
        {coordinateTarget && <section className="study-coordinate-guide" aria-live="polite">
          <span>좌표 안내</span>
          <strong>{coordinateTarget.file + 1}선 {coordinateTarget.rank + 1}단</strong>
          <p><b>{coordinateTarget.file + 1}선</b>은 왼쪽에서 {coordinateTarget.file + 1}번째, <b>{coordinateTarget.rank + 1}단</b>은 위에서 {coordinateTarget.rank + 1}번째 교차점입니다.</p>
          <small>파란 원이 목표 위치입니다. 노란 점선이 보이면 움직일 기물의 출발점입니다.</small>
        </section>}
        <GameBoard pieces={current.pieces} perspective={perspective} interactiveSide={current.sideToMove} allowedMoves={analysis ? engineMoves : undefined} guides={boardGuides} onMove={move}/>
        <div className="study-board-tools"><button disabled={index === 0} onClick={() => setIndex(0)}>처음</button><button disabled={index === 0} onClick={() => setIndex((value) => value - 1)}>이전</button><button disabled={index === timeline.length - 1} onClick={() => setIndex((value) => value + 1)}>다음</button><button disabled={index === timeline.length - 1} onClick={() => setIndex(timeline.length - 1)}>끝</button><button onClick={() => setPerspective((side) => side === 'cho' ? 'han' : 'cho')}>판 뒤집기</button><button onClick={reset}>다시 시작</button></div>
      </main>

      <aside className="study-panel">
        <nav><button className={tab === 'lesson' ? 'active' : ''} onClick={() => setTab('lesson')}>강의</button><button className={tab === 'engine' ? 'active' : ''} onClick={() => setTab('engine')}>엔진</button><button className={tab === 'moves' ? 'active' : ''} onClick={() => setTab('moves')}>기보</button></nav>
        {tab === 'lesson' && <LessonNotes lesson={lesson} status={status} onComplete={markComplete}/>} 
        {tab === 'engine' && <div className="study-engine">
          <header><div><span>Fairy-Stockfish</span><strong>{loading ? '계산 중' : leading.text}</strong></div><small>깊이 {analysis?.depth || 0} · 후보 3개</small></header>
          {engineError && <p className="study-engine__error">{engineError}</p>}
          <div className="study-engine__lines">{analysis?.lines.map((line) => { const score = lineScore(line, current.sideToMove); return <button key={line.multipv} onClick={() => line.pv[0] && playUci(line.pv[0])}><b>{line.multipv}</b><div><strong>{score.text}</strong><p>{formatEngineLine(line, current.pieces).map((item) => <span key={item}>{item}</span>)}</p></div></button>; }) || <p>후보 수를 계산하고 있습니다.</p>}</div>
          <button className="study-analysis-button" onClick={openAnalysis}>이 위치를 분석판에서 계속 보기</button>
        </div>}
        {tab === 'moves' && <div className="study-moves"><button className={index === 0 ? 'active' : ''} onClick={() => setIndex(0)}><span>0</span><strong>시작 위치</strong></button>{timeline.slice(1).map((position, moveIndex) => <button className={index === moveIndex + 1 ? 'active' : ''} key={`${position.moveUci}-${moveIndex}`} onClick={() => setIndex(moveIndex + 1)}><span>{moveIndex + 1}</span><strong>{position.move}</strong></button>)}</div>}
        <footer><Link className={!previous ? 'disabled' : ''} to={previous ? lessonHref(previous.course.id, previous.lesson.id) : '#'}>← 이전 강의</Link>{completed.includes(lesson.id) && <span>✓ 완료</span>}<Link className={!next ? 'disabled' : ''} to={next ? lessonHref(next.course.id, next.lesson.id) : '#'}>다음 강의 →</Link></footer>
      </aside>
    </div>
  </div>;
}
